import { AlertCircle, CheckCircle2, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { PageDescription, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { getAuthProviderIcon, getAuthProviderBenefit } from '@/lib/plugin-registry';
import { getProviderState, type LinkedAccount, type ProviderEntry } from './provider-state';

interface ProviderAccountListProps {
  providers: ProviderEntry[];
  linkedAccounts: LinkedAccount[];
  onConnect: (provider: string) => void;
  onUnlink?: (provider: string) => void;
  unlinkDisabled?: boolean;
  unlinkPending?: boolean;
  connectingProvider?: string | null;
  showBenefits?: boolean;
}

export function ProviderAccountList({
  providers,
  linkedAccounts,
  onConnect,
  onUnlink,
  unlinkDisabled,
  unlinkPending,
  connectingProvider,
  showBenefits = false,
}: ProviderAccountListProps) {
  const linkedByProvider = new Map(linkedAccounts.map((p) => [p.provider, p]));

  const sortedProviders = [...providers].sort((a, b) => {
    const aLinked = linkedByProvider.has(a.name) ? 0 : 1;
    const bLinked = linkedByProvider.has(b.name) ? 0 : 1;
    return aLinked - bLinked;
  });

  return (
    <div className="flex flex-col gap-3">
      {sortedProviders.map((provider) => {
        const { name, label } = provider;
        const Icon = getAuthProviderIcon(name);
        const benefit = getAuthProviderBenefit(name);
        const linked = linkedByProvider.get(name);
        const state = getProviderState(provider, linked);

        return (
          <div
            key={name}
            id={`provider-row-${name}`}
            data-testid={`provider-row-${name}`}
            data-linked={linked ? 'true' : 'false'}
            data-state={state}
            className="flex flex-col gap-3 rounded-lg border p-3 min-[440px]:flex-row min-[440px]:items-center min-[440px]:justify-between"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                {Icon ? (
                  <Icon className="h-4 w-4" />
                ) : (
                  <img
                    src={`/plugin-icons/plugin-icon-auth-${name}.png`}
                    alt=""
                    className="h-5 w-5 object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <PanelTitle as="h3" size="sm" className="min-w-0">
                    {label}
                  </PanelTitle>
                  {state === 'connected' && (
                    <Badge variant="secondary" className="gap-1 text-3xs">
                      <CheckCircle2 className="h-3 w-3 text-status-success" />
                      Connected
                    </Badge>
                  )}
                  {state === 'reauth' && (
                    <Badge variant="secondary" className="gap-1 text-3xs text-status-warning">
                      <AlertCircle className="h-3 w-3" />
                      Reconnect needed
                    </Badge>
                  )}
                </div>
                {linked ? (
                  <PageDescription className="mt-0 text-xs">
                    {(linked.providerData as { login?: string })?.login ?? linked.providerUserId}
                  </PageDescription>
                ) : showBenefits && benefit ? (
                  <PageDescription className="mt-0 text-xs">{benefit}</PageDescription>
                ) : null}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 self-stretch min-[440px]:self-auto">
              {state === 'reauth' && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => onConnect(name)}
                  disabled={connectingProvider === name}
                  aria-label={`Reconnect ${label}`}
                  className="ml-auto"
                >
                  {connectingProvider === name ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <>
                      <RefreshCw className="size-3.5" />
                      Reconnect
                    </>
                  )}
                </Button>
              )}
              {linked && onUnlink && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onUnlink(name)}
                  disabled={unlinkDisabled || unlinkPending}
                  aria-label={`Unlink ${label}`}
                  className={state === 'connected' ? 'ml-auto' : ''}
                >
                  {unlinkPending ? <Spinner className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              )}
              {state === 'disconnected' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onConnect(name)}
                  disabled={connectingProvider === name}
                  className="w-full min-[440px]:w-auto"
                >
                  {connectingProvider === name ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <>
                      <ExternalLink className="size-3.5" />
                      Connect
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
